// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { arrayMove } from "@dnd-kit/sortable";
import { differenceInDays, startOfToday } from "date-fns";
import { compact, difference, intersection, isEmpty } from "lodash";
import { action, makeAutoObservable, reaction } from "mobx";
import pluralize from "pluralize";
import toast from "react-hot-toast";

import { OpportunityType } from "~datatypes";

import { FilterField, FilterOption, FilterType } from "../../core/models/types";
import {
  OpportunityTableColumnDef,
  OpportunityTableColumnId,
} from "../../core/OpportunityCaseloadView/HydratedOpportunityPersonList";
import { insightsUrl, workflowsUrl } from "../../core/views";
import { formatSupervisionEndDatePhrase } from "../../core/WorkflowsJusticeInvolvedPersonProfile/utils";
import { FilterPresenter } from "../../FilterStore/FilterPresenter";
import OpportunitiesFilterStore from "../../FilterStore/OpportunitiesFilterStore";
import FirestoreStore, { UserUpdateRecord } from "../../FirestoreStore";
import { SupervisionOpportunityPresenter } from "../../InsightsStore/presenters/SupervisionOpportunityPresenter";
import { SupervisionSupervisorOpportunityPresenter } from "../../InsightsStore/presenters/SupervisionSupervisorOpportunityPresenter";
import AnalyticsStore from "../../RootStore/AnalyticsStore";
import TenantStore from "../../RootStore/TenantStore";
import { FeatureVariantRecord } from "../../RootStore/types";
import { toTitleCase } from "../../utils";
import {
  Opportunity,
  OpportunityTab,
  OpportunityTabGroup,
} from "../Opportunity";
import { OpportunityConfiguration } from "../Opportunity/OpportunityConfigurations";
import { CollectionDocumentSubscription } from "../subscriptions";
import { JusticeInvolvedPerson } from "../types";
import { opportunitiesBySubcategory, opportunitiesByTab } from "../utils";
import { WorkflowsStore } from "../WorkflowsStore";
import {
  TableViewSelectInterface,
  TableViewSelectPresenter,
} from "./TableViewSelectPresenter";

type OpportunityPersonListPresenterOptions = {
  initialTab?: string;
};

/**
 * Responsible for presenting information about the caseload relative to a user's
 * current view of a single opportunity, including who is eligible and the user's
 * visual settings such as tab grouping and tab ordering.
 */
export class OpportunityPersonListPresenter
  implements
    TableViewSelectInterface,
    FilterPresenter<OpportunitiesFilterStore>
{
  readonly isSupervisorHomepage: boolean;
  readonly displayTabGroups: OpportunityTabGroup[];
  readonly showZeroGrantsPill: boolean;
  private _activeTabGroup: OpportunityTabGroup;
  private userSelectedTab?: OpportunityTab;
  private userOrderedTabs?: OpportunityTab[];
  private readonly updatesSubscription?: CollectionDocumentSubscription<UserUpdateRecord>;
  private tableViewSelectPresenter: TableViewSelectPresenter;

  private _navigablePeople: Opportunity<JusticeInvolvedPerson>[] = [];

  constructor(
    private readonly analyticsStore: AnalyticsStore,
    private readonly firestoreStore: FirestoreStore,
    private readonly tenantStore: TenantStore,
    readonly filterStore: OpportunitiesFilterStore,
    private readonly workflowsStore: WorkflowsStore,
    public readonly config: OpportunityConfiguration,
    featureVariants: FeatureVariantRecord,
    public readonly opportunityType: OpportunityType,
    private readonly supervisionPresenter?:
      | SupervisionOpportunityPresenter
      | SupervisionSupervisorOpportunityPresenter,
    { initialTab }: OpportunityPersonListPresenterOptions = {},
  ) {
    this.isSupervisorHomepage = !!supervisionPresenter;

    this.displayTabGroups = Object.keys(
      config.tabGroups,
    ) as OpportunityTabGroup[];
    // TODO (OBT-39704) Refactor OpportunityTabGroup to enums
    this._activeTabGroup =
      this.supervisionPresenter &&
      "defaultTabGroup" in this.supervisionPresenter
        ? this.supervisionPresenter.defaultTabGroup
        : this.displayTabGroups[0];
    this.userOrderedTabs = undefined;
    if (initialTab) {
      this.activeTab = initialTab as OpportunityTab;
    }

    this.tableViewSelectPresenter = new TableViewSelectPresenter(
      firestoreStore,
      workflowsStore,
      featureVariants,
    );

    this.updatesSubscription = this.workflowsStore.userUpdatesSubscription;

    this.showZeroGrantsPill = !!(
      featureVariants.zeroGrantsFlag &&
      this.officerRecord?.zeroGrantOpportunities?.includes(opportunityType)
    );

    // only update the list of opportunities to navigate through when necessary,
    // to avoid changing the list when an opportunity status is changed from side panel
    reaction(
      () => this.selectedOpportunity,
      (nextOpp) => {
        if (!nextOpp || !this._navigablePeople.includes(nextOpp))
          this.updateNavigablePeople();
      },
      // populate the list if there's already a selected person upon page load
      { fireImmediately: true },
    );

    makeAutoObservable(this, {
      updateNavigablePeople: action,
      handleOpportunityClick: action,
      handleTabClick: action,
      // Always returns the same constant; not derived from any observable
      // state, so making it a computed just trips MobX's empty-derivation
      // warning.
      almostEligibilityDateHeader: false,
    });
  }

  get showListView() {
    return this.tableViewSelectPresenter.showListView;
  }

  set showListView(showListView: boolean) {
    this.analyticsStore.trackOpportunityTableViewPreferenceChanged({
      newViewType: showListView ? "list" : "table",
      oldViewType: this.showListView ? "list" : "table",
      opportunityType: this.opportunityType,
    });
    this.tableViewSelectPresenter.showListView = showListView;
  }

  /**
   * Update the ordered list of column IDs to render in the opportunity table view
   * after dynamic checks, filtering out any IDs that should not be rendered due to
   * opportunity or application state.
   */
  get enabledColumnIds(): Array<OpportunityTableColumnId> {
    const opportunities = this.peopleInActiveTab;
    // List of column IDs from base/custom config
    let columnIds = [...this.config.enabledColumns];

    // Helper function to check a particular dynamic condition for this column. When the condition is not met, filters out the ID from the list of columns to be rendered.
    const overrideColumn = (
      id: OpportunityTableColumnId,
      enabled: () => boolean,
    ) => {
      // Only calls predicate enabled() function if column IDs list contains the relevant ID.
      if (columnIds.includes(id) && !enabled()) {
        columnIds = columnIds.filter((c) => c !== id);
      }
    };

    overrideColumn("INSTANCE_DETAILS", () =>
      opportunities.some((opp) => !!opp.instanceDetails),
    );
    overrideColumn(
      "ASSIGNED_STAFF_NAME",
      () =>
        (this.isSupervisorHomepage && !!this.supervisorInfo) ||
        (!this.isSupervisorHomepage &&
          opportunities.some((opp) => !!opp.person.assignedStaffId)),
    );
    overrideColumn("ALMOST_ELIGIBLE_STATUS", () =>
      opportunities.some(
        (opp) =>
          !opp.denied && !opp.isSubmitted && opp.almostEligibleStatusMessage,
      ),
    );
    // Conditional predicate function used for multiple columns.
    const almostEligibilityDateEnabled = () =>
      this.config.enabledColumns.includes("ALMOST_ELIGIBILITY_DATE") &&
      opportunities.some(
        (opp) => opp.almostEligible && !!opp.almostEligibilityDate,
      );
    overrideColumn("ALMOST_ELIGIBILITY_DATE", almostEligibilityDateEnabled);
    overrideColumn(
      "ELIGIBILITY_DATE",
      () =>
        !almostEligibilityDateEnabled() &&
        opportunities.some((opp) => !!opp.eligibilityDate),
    );
    overrideColumn(
      "SNOOZE_ENDS_IN",
      () =>
        this.isViewingDeniedTab &&
        opportunities.some(
          (opp: Opportunity) =>
            !!this.snoozeEndsInDays(opp) ||
            !isEmpty(opp.indefiniteDenialReasons),
        ),
    );
    overrideColumn("SUBMITTED_FOR", () => this.isViewingSubmittedTab);
    overrideColumn("DENIAL_REASONS", () => this.isViewingDeniedTab);

    // Conditional predicate function used for multiple columns.
    const allInReviewOrRevisions = () =>
      opportunities.every(
        (opp) => opp.isInSupervisorReview || opp.isInRevisionsRequested,
      );
    overrideColumn("US_TX_CURRENT_REVIEWER", allInReviewOrRevisions);
    overrideColumn("US_TX_SUBMITTED_FOR_REVIEW_DATE", allInReviewOrRevisions);
    overrideColumn("US_TX_ALL_REVIEWERS", () =>
      opportunities.every(
        (opp) =>
          opp.isInSupervisorReview ||
          opp.isInRevisionsRequested ||
          opp.isGrantApproved,
      ),
    );
    overrideColumn("US_TX_GRANT_DATE", () =>
      opportunities.every((opp) => opp.isGrantApproved),
    );
    overrideColumn("US_TX_REVISION_REASON", () =>
      opportunities.every((opp) => opp.isInRevisionsRequested),
    );

    // The CTA button column should be last to take advantage of special rightmost column formatting
    if (columnIds.includes("CTA_BUTTON")) {
      columnIds = columnIds.filter((c) => c !== "CTA_BUTTON");
      columnIds.push("CTA_BUTTON");
    }

    return columnIds;
  }

  /**
   * When the API does not provide enabled columns, render defaults in `allColumns` order; otherwise use `enabledColumnIds` order.
   */
  orderedColumnDefs(
    allColumns: OpportunityTableColumnDef[],
  ): OpportunityTableColumnDef[] {
    // TODO(OBT-39443): Add enabledColumns in the admin panel configs rather than relying on static defaults
    if (!this.config.apiEnabledColumnIds.length)
      return allColumns.filter((col) => this.enabledColumnIds.includes(col.id));
    return compact(
      this.enabledColumnIds.map((id) =>
        allColumns.find((col) => col.id === id),
      ),
    );
  }

  /**
   * Returns a custom initial state for the opportunity table view based on the opportunity type
   */
  get initialTableState() {
    switch (this.opportunityType) {
      case "usIaEarlyDischarge":
        return {
          sorting: [{ id: "SUPERVISION_EXPIRATION_DATE", desc: false }],
        };
      case "usTnCustodyLevelDowngrade2026Policy":
        return {
          sorting: [{ id: "US_TN_LATEST_CLASSIFICATION_DATE", desc: true }],
        };
      case "usMiSecurityClassificationCommitteeReviewV2":
      case "usMiWardenInPersonSecurityClassificationCommitteeReviewV2":
      case "usMiAddInPersonSecurityClassificationCommitteeReviewV2":
        return {
          sorting: [{ id: "US_MI_NEXT_SCC_DATE", desc: false }],
        };
    }
    return undefined;
  }

  get progressiveLoadingConfig() {
    return {
      enabled: this.config.enableProgressiveLoading,
      batchSize: this.config.progressiveLoadingBatchSize,
    };
  }

  submittedForDays(opp: Opportunity): number | undefined {
    if (!opp.submittedUpdate) return;
    return differenceInDays(opp.submittedUpdate.date.toDate(), startOfToday());
  }

  snoozeEndsInDays(opp: Opportunity): number | undefined {
    if (opp.manualSnooze) return opp.snoozeForDays;
    if (opp.autoSnooze && opp.autoSnooze.snoozeUntil) {
      return differenceInDays(
        startOfToday(),
        new Date(opp.autoSnooze.snoozeUntil),
      );
    }
  }

  get isViewingDeniedTab() {
    return this.activeTab === this.config.deniedTabTitle;
  }

  get isViewingSubmittedTab() {
    return this.activeTab === this.config.submittedTabTitle;
  }

  get activeTab() {
    // The currently active tab is either the user's selection or the default tab.
    // We switch to the default tab when the user has not clicked anything or
    // when the user-selected tab is no longer displayed (either when the user
    // switches tab groups, or when the current tab group disallows showing empty
    // tabs and the user-selected tab becomes empty upon removing a search item)
    return this.userSelectedTab &&
      this.displayTabs.includes(this.userSelectedTab)
      ? this.userSelectedTab
      : this.defaultTab;
  }

  set activeTab(newTab: OpportunityTab) {
    this.userSelectedTab = newTab;
  }

  handleTabClick(newTab: OpportunityTab) {
    this.activeTab = newTab;
    this.analyticsStore.trackOpportunityTabClicked({ tab: newTab });
  }

  get displayTabs() {
    if (this.shouldShowAllTabs) {
      // Keep track of the user's selection locally to avoid
      // flickering when waiting for the user's tab preference to update in firestore
      return this.userOrderedTabs || this.defaultOrderedTabs;
    } else {
      return this.unorderedTabs;
    }
  }

  set displayTabs(newTabOrder: OpportunityTab[]) {
    this.userOrderedTabs = newTabOrder;
    if (this.shouldShowAllTabs) {
      this.firestoreStore
        .updateCustomTabOrderings(
          this.opportunityType,
          this.activeTabGroup,
          newTabOrder,
        )
        .then(() => {
          toast(`New tab ordering has been saved for ${this.config.label}`, {
            id: "newTabOrderingToast", // prevent duplicate toasts
            position: "bottom-left",
          });

          this.analyticsStore.trackOpportunityTabOrderChanged({
            tabOrder: newTabOrder,
            opportunityType: this.opportunityType,
          });
        })
        .catch((e: Error) => {
          toast("Failed to save new tab ordering: " + e.message, {
            position: "bottom-left",
          });
        });
    }
  }

  get activeTabGroup() {
    return this._activeTabGroup;
  }

  set activeTabGroup(newTabGroup: OpportunityTabGroup) {
    if (newTabGroup !== this._activeTabGroup) {
      this._activeTabGroup = newTabGroup;
      // The user's tab order preference may have changed based on this new tab group
      this.userOrderedTabs = undefined;
    }
  }

  private get defaultTab() {
    if (
      this.supervisorInfo &&
      this.config.supervisorReviewTabTitle &&
      this.displayTabs.includes(this.config.supervisorReviewTabTitle)
    ) {
      return this.config.supervisorReviewTabTitle;
    }
    return this.displayTabs[0];
  }

  private get defaultOrderedTabs(): OpportunityTab[] {
    const customOrder =
      this.updatesSubscription?.data?.customTabOrderings?.[
        this.opportunityType
      ]?.[this.activeTabGroup] ?? [];
    const configOrder = this.config.tabGroups[this.activeTabGroup] ?? [];
    return [
      // Only list tabs that are included in the config
      // First list tabs that exist in the custom order
      ...intersection(customOrder, configOrder),
      // Then list those that don't
      ...difference(configOrder, customOrder),
    ];
  }

  private get unorderedTabs(): OpportunityTab[] {
    // Only display tabs in the active tab group that correspond to relevant
    // opportunities
    const tabs = this.oppsFromOpportunitiesByTab
      ? (intersection(
          this.config.tabGroups[this.activeTabGroup],
          Object.keys(this.oppsFromOpportunitiesByTab),
        ) as OpportunityTab[])
      : [];
    return tabs;
  }

  get shouldShowAllTabs() {
    return this.activeTabGroup === "ELIGIBILITY STATUS";
  }

  get activeOpportunityNotifications() {
    return this.workflowsStore.activeNotificationsForOpportunityType(
      this.opportunityType,
    );
  }

  /**
   * @return A map from tabs to the value to display on that tab's badge,
   * i.e. the number of people in that tab
   */
  get tabBadges(): Partial<Record<OpportunityTab, number>> {
    const badges: Partial<Record<OpportunityTab, number>> = {};
    for (const tab of this.displayTabs) {
      badges[tab] = this.oppsFromOpportunitiesByTab?.[tab]?.length ?? 0;
    }
    return badges;
  }

  get oppsFromOpportunitiesByTab():
    | Record<OpportunityTab, Opportunity[]>
    | undefined {
    const oppsByType =
      this.supervisionPresenter?.opportunitiesByType ??
      this.workflowsStore.allOpportunitiesByType;

    const allOppsForTypeByTab = opportunitiesByTab(
      oppsByType,
      this.activeTabGroup,
      this.supervisorInfo?.externalId,
    )[this.opportunityType];

    const { deniedTabTitle, excludeIndefiniteSnoozesFromTableView } =
      this.config;

    // Filter out indefinite snoozes from the denied tab if relevant
    if (
      allOppsForTypeByTab &&
      excludeIndefiniteSnoozesFromTableView &&
      deniedTabTitle in allOppsForTypeByTab
    ) {
      return {
        ...allOppsForTypeByTab,
        [deniedTabTitle]: allOppsForTypeByTab[deniedTabTitle].filter(
          (opp) => !opp.isIndefinitelySnoozed,
        ),
      };
    }

    return allOppsForTypeByTab;
  }

  /**
   * Returns the supervision officer record from the supervision presenter if one
   * exists.
   *
   * Returns undefined otherwise (i.e. if there is no presenter or the presenter
   * does not have an officer record).
   */
  get officerRecord() {
    if (
      this.supervisionPresenter &&
      "officerRecord" in this.supervisionPresenter
    ) {
      return this.supervisionPresenter.officerRecord;
    }
    return undefined;
  }

  /**
   * Returns the supervision supervisor record from the supervision presenter if one
   * exists.
   *
   * Returns undefined otherwise (i.e. if there is no presenter or the presenter
   * does not have an supervisor record).
   */
  get supervisorInfo() {
    if (
      this.supervisionPresenter &&
      "supervisorInfo" in this.supervisionPresenter
    )
      return this.supervisionPresenter.supervisorInfo;
    else return undefined;
  }

  get label() {
    if (this.officerRecord) {
      return `${this.officerRecord.displayName}, ${this.config.label}`;
    }
    return this.config.label;
  }

  get callToAction() {
    return this.config.callToAction;
  }

  get subheading() {
    return this.config.subheading;
  }

  get zeroGrantsTooltip() {
    return this.config.zeroGrantsTooltip;
  }

  get eligibilityDateHeader() {
    // Header text for the "eligibility date" column in table view
    return (
      this.config.eligibilityDateTextForTab(this.activeTab) ??
      "Eligibility Date"
    );
  }

  get almostEligibilityDateHeader() {
    // Header text for the "eligibility date" column in table view
    return "Date Surfaced";
  }

  get releaseDateHeader() {
    // Header text for the "release date" column in table view
    return this.tenantStore.labels.releaseDateCopy;
  }

  get assignedToHeader() {
    // Header text for the "assigned to" column in table view
    return this.tenantStore.labels.assignedToCopy;
  }

  get supervisionEndHeader() {
    // Header text for the "supervision end date" column in table view
    return formatSupervisionEndDatePhrase(
      this.tenantStore.labels.supervisionEndDateCopy,
    );
  }

  get displayIdHeader() {
    // Header text for the "display ID" column in table view
    return this.tenantStore.getDisplayIdCopy(this.config.systemType);
  }

  get submittedForHeader() {
    // Header text for the "submitted" column in table view
    const { submittedTabTitle } = this.config;
    return `${toTitleCase(submittedTabTitle.toLowerCase())} for`;
  }

  get peopleInActiveTab() {
    return this.oppsFromOpportunitiesByTab?.[this.activeTab] ?? [];
  }

  get peopleInActiveTabBySubcategory() {
    return opportunitiesBySubcategory(this.peopleInActiveTab);
  }

  get subcategoryOrder(): string[] | undefined {
    const peopleBySubcategory = this.peopleInActiveTabBySubcategory;
    if (!peopleBySubcategory) return undefined;

    // Respect any order defined in the configuration if one exists
    return (
      this.config.subcategoryOrderings?.[this.activeTab] ??
      Object.keys(peopleBySubcategory)
    );
  }

  get navigablePeople() {
    return this._navigablePeople;
  }

  /**
   * Set the list of people that a user can navigate through to the current contents of
   * the active tab, in display order (taking into account subcategories if applicable).
   * Called when a new preview modal is opened, so that the list of navigable
   * people does not change even if people move in/out of the active tab.
   */
  updateNavigablePeople() {
    const { peopleInActiveTabBySubcategory, subcategoryOrder } = this;

    // the subcategory order should always be defined if there are currently
    // subcategories
    if (peopleInActiveTabBySubcategory && subcategoryOrder) {
      this._navigablePeople = subcategoryOrder.flatMap(
        (category) => peopleInActiveTabBySubcategory[category] ?? [],
      );
    } else {
      this._navigablePeople = this.orderedOpportunitiesForSelectedCategory()
        // Filter out consecutive repeats
        .filter((client, i, allClients) => {
          return i === 0 || client !== allClients[i - 1];
        });
    }
  }

  orderedOpportunitiesForSelectedCategory(subcategory?: string): Opportunity[] {
    let opps: Opportunity[];
    if (
      subcategory &&
      this.peopleInActiveTabBySubcategory &&
      this.peopleInActiveTabBySubcategory[subcategory]
    ) {
      opps = this.peopleInActiveTabBySubcategory[subcategory];
    } else if (subcategory) {
      // We are within a subcategory section, but there are no active clients in this subcategory
      opps = [];
    } else {
      opps = this.peopleInActiveTab;
    }

    return this.filterStore.orderedOpportunitiesForCategory(
      this.selectedCategory,
      opps,
    );
  }

  headingText(subcategory: string) {
    return this.config.subcategoryHeadings?.[subcategory];
  }

  get justiceInvolvedPersonTitle() {
    if (this.supervisionPresenter) {
      return this.supervisionPresenter.labels.supervisionJiiLabel;
    } else {
      return this.workflowsStore.justiceInvolvedPersonTitle;
    }
  }

  get searchIdsNotSelectedCtaText() {
    const {
      searchStore: { workflowsSearchFieldTitle, isTypesenseSearchEnabled },
      justiceInvolvedPersonTitle,
    } = this.workflowsStore;

    return isTypesenseSearchEnabled && workflowsSearchFieldTitle
      ? `Start typing the name of ${workflowsSearchFieldTitle} above to review and refer eligible ${pluralize(justiceInvolvedPersonTitle)} for opportunities.`
      : `Search above to review and refer eligible ${pluralize(justiceInvolvedPersonTitle)} for opportunities.`;
  }

  get emptyTabText() {
    if (this.activeTab) {
      return (
        this.config.emptyTabCopy[this.activeTab] ??
        `At this time, there are no ${this.justiceInvolvedPersonTitle}s who are ${this.activeTab}. Please navigate to one of the other tabs.`
      );
    }
    if (this.config.hideWorkflowsTabGroupSelector) {
      // Right now, we only expect to hit this case in US_TX
      // We hit this case when this.activeTab is undefined, and hideWorkflowsTabGroupSelector is true
      // this.activeTab is undefined for Texas in Insights when there are no more opportunities left for a supervisor to review
      return `At this time, there are no ${this.justiceInvolvedPersonTitle}s who are awaiting your review.`;
    }
    // When we don't have an active tab, there are no people in our current tab group
    // We expect this case for states other than US_TX, where hideWorkflowsTabGroupSelector if false
    // In this case, there are no opportunities for the current grouping, but users can choose another grouping using the WorkflowsTabGroupSelector
    return `Please select a different grouping. None of the ${this.justiceInvolvedPersonTitle}s were able to be grouped by ${toTitleCase(this.activeTabGroup.toLowerCase())}.`;
  }

  get displayedOpportunityCategories(): OpportunityTabGroup[] {
    return this.displayTabGroups;
  }

  get selectedCategory() {
    return (
      this._activeTabGroup ??
      // If the user hasn't selected anything, default to the first non-empty category
      this.displayedOpportunityCategories.find(
        (category) =>
          this.filterStore.countForOpportunityCategory(
            category,
            this.peopleInActiveTab,
          ) > 0,
      ) ??
      this.displayedOpportunityCategories[0]
    );
  }

  get selectedSearchablesCount() {
    // The opportunity person list within the supervisor homepage is officer-specific, so
    // there will be exactly 1 officer selected, even though nobody has been searched for
    if (this.isSupervisorHomepage) return 1;
    return this.workflowsStore.searchStore.selectedSearchables.length;
  }

  get workflowsSearchFieldTitle() {
    return this.workflowsStore.searchStore.workflowsSearchFieldTitle;
  }

  get tabPrefaceText(): string | undefined {
    if (this.activeTab) {
      return this.config.tabPrefaceCopy[this.activeTab];
    }
  }

  get selectedPerson() {
    // TODO(#5965): Look into isolating the selected person from the workflows store
    if (this.supervisionPresenter) {
      // Pull the selected client from the presenter's hydrated list of clients
      // so that we can access the relevant hydrated opportunity when selected.
      return this.supervisionPresenter.clients?.find(
        (client) =>
          client.pseudonymizedId ===
          this.workflowsStore.selectedPerson?.pseudonymizedId,
      );
    } else {
      return this.workflowsStore.selectedPerson;
    }
  }

  get selectedOpportunity() {
    // We cannot use the supervisionPresenter's selectedOpportunity because the
    // CaseloadOpportunityCell/CaseloadTable change the selected opp in the workflows store;
    // this method will still always return a hydrated opportunity because the
    // selected person is from the correct source and therefore hydrated
    return this.selectedPerson?.opportunities[this.opportunityType]?.find(
      (opp) => opp.selectId === this.workflowsStore.selectedOpportunityId,
    );
  }

  get selectedOpportunityType() {
    if (this.supervisionPresenter) {
      return this.supervisionPresenter.opportunityType;
    }
    return this.workflowsStore.selectedOpportunityType;
  }

  get opportunityConfigs() {
    if (this.supervisionPresenter) {
      // @ts-expect-error accessing private opportunity configuration store
      // because workflows configurations are not hydrated on supervisor homepage
      return this.supervisionPresenter.opportunityConfigurationStore
        ?.apiOpportunityConfigurations;
    }
    return this.workflowsStore.opportunityConfigurationStore.opportunities;
  }

  get opportunityTypes(): OpportunityType[] {
    if (this.supervisionPresenter) {
      return Object.keys(
        this.supervisionPresenter.opportunitiesByType ?? {},
      ) as OpportunityType[];
    }
    return this.workflowsStore.opportunityTypes;
  }

  urlForOppConfig(config: OpportunityConfiguration) {
    if (this.officerRecord) {
      return insightsUrl("supervisionOpportunity", {
        officerPseudoId: this.officerRecord.pseudonymizedId,
        opportunityTypeUrl: config.urlSection,
      });
    }
    if (this.supervisorInfo) {
      return insightsUrl("supervisionSupervisorOpportunity", {
        supervisorPseudoId: this.supervisorInfo.pseudonymizedId,
        opportunityTypeUrl: config.urlSection,
      });
    }
    return workflowsUrl("opportunityClients", {
      urlSection: config.urlSection,
    });
  }

  get overdueOpportunityUrl(): string | undefined {
    if (!this.config.linkedOverdueOpportunityType) return;
    const urlSection =
      this.workflowsStore.opportunityConfigurationStore.opportunities[
        this.config.linkedOverdueOpportunityType
      ].urlSection;
    return workflowsUrl("opportunityClients", { urlSection });
  }

  get overdueOpportunityCount(): number {
    const { linkedOverdueOpportunityType } = this.config;
    if (!linkedOverdueOpportunityType) return 0;

    return (
      this.workflowsStore.eligibleOpportunities[linkedOverdueOpportunityType]
        ?.length || 0
    );
  }

  get overdueOpportunityCalloutCopy() {
    return this.config.overdueOpportunityCalloutCopy;
  }

  get hasMultipleDistinctStatusesInTab() {
    return (
      new Set(
        this.peopleInActiveTab.map((person) => person.eligibilityStatusLabel()),
      ).size > 1
    );
  }

  dismissNotification(id: string) {
    this.workflowsStore.dismissOpportunityNotification(id);
  }

  swapTabs(dragged: OpportunityTab, newLocation: OpportunityTab) {
    const oldIndex = this.displayTabs.indexOf(dragged);
    const newIndex = this.displayTabs.indexOf(newLocation);
    const newTabs = arrayMove(this.displayTabs, oldIndex, newIndex);
    this.displayTabs = newTabs;
  }

  handleOpportunityClick(opp: Opportunity) {
    this.workflowsStore.updateSelectedPersonAndOpportunity(opp);
  }

  shouldHighlightOpportunity(opp: Opportunity) {
    return (
      opp.person.pseudonymizedId === this.selectedPerson?.pseudonymizedId &&
      opp.selectId === this.selectedOpportunity?.selectId
    );
  }

  // Filtering

  // Return the number of total opportunities, regardless of the current category and filters.
  numItems(type: FilterType, field: FilterField, option: FilterOption): number {
    const allOpportunities = this.filterStore.allOpportunitiesForCategory(
      "ELIGIBILITY STATUS",
      this.peopleInActiveTab,
      false,
    );

    return allOpportunities.filter((opp) => {
      if (type === "opportunity") {
        if (opp.record?.caseNotes[field]) {
          return opp.record.caseNotes[field][0].noteTitle === option.value;
        }
      } else if (type === "person") {
        // @ts-expect-error we don't currently narrow the type of field adequately
        // but field should always be FilterFieldForPerson here
        return opp.person[field] === option.value;
      }
      return false;
    }).length;
  }

  trackFilterDropdownOpened() {
    return this.filterStore.trackOpportunityFilterDropdownOpened;
  }
}
